import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { TimezoneDisplay, TimezoneTime } from '../../shared/TimezoneIndicator';
import { Activity, Send, MessageSquare, Clock } from 'lucide-react';

export function OverviewTab({
  exercise, injects, responses, timeline,
  isAdmin, isObserver, canManageExercise,
  openAssignDialog, openAssignControllersDialog, openAssignObserversDialog,
  getStatusBadgeClass,
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-secondary">Scenario Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {exercise.scenario_summary || 'No scenario summary provided.'}
            </p>
            {exercise.description && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground">{exercise.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-secondary">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="capitalize">{exercise.exercise_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge className={`${getStatusBadgeClass(exercise.status)} border capitalize`}>
                {exercise.status}
              </Badge>
            </div>
            <div className="border-t border-b py-3 my-2">
              <TimezoneDisplay timezone={exercise.timezone || 'UTC'} showRelative={true} />
            </div>
            {exercise.start_date && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Start</span>
                <TimezoneTime time={exercise.start_date} timezone={exercise.timezone || 'UTC'} />
              </div>
            )}
            {exercise.end_date && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">End</span>
                <TimezoneTime time={exercise.end_date} timezone={exercise.timezone || 'UTC'} />
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Participants</span>
              <div className="flex items-center gap-2">
                <span>{exercise.assigned_users?.length || 0}</span>
                {canManageExercise && (
                  <Button size="sm" variant="outline" onClick={openAssignDialog}>Assign</Button>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Controllers (Simcell)</span>
                <div className="flex items-center gap-2">
                  <span>{exercise.assigned_controllers?.length || 0}</span>
                  <Button size="sm" variant="outline" onClick={openAssignControllersDialog}>Assign</Button>
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Observers</span>
                <div className="flex items-center gap-2">
                  <span>{exercise.assigned_observers?.length || 0}</span>
                  <Button size="sm" variant="outline" onClick={openAssignObserversDialog}>Assign</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {!isObserver() && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="tactical-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Activity className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase">Injects</p>
                  <p className="font-secondary font-bold text-2xl">{injects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="tactical-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Send className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase">Sent</p>
                  <p className="font-secondary font-bold text-2xl">
                    {injects.filter(i => i.status !== 'draft').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="tactical-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-green-500" />
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase">Responses</p>
                  <p className="font-secondary font-bold text-2xl">{responses.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="tactical-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase">Events</p>
                  <p className="font-secondary font-bold text-2xl">{timeline.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
